import React, { useState, useEffect } from 'react';
import { Users, DollarSign, Calendar, TrendingUp, X } from 'lucide-react';
import { Modal, Button, LoadingSpinner, DataTable } from '@/components/ui';
import { GlassCard } from '@/design-system/components/primitives';
import type { Team, ResourceTeamAssignment, RateCard, Column } from '@/types';
import { resourceTeamAssignmentsApi, rateCardsApi } from '@/utils/api';

interface TeamDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
}

interface RosterItem {
  id: string;
  resourceName: string;
  resourceId: string;
  resourceType: string;
  resourceLevel: string;
  startDate: string;
  hourlyRate: number | null;
  annualCost: number | null;
}

interface CostBreakdown {
  level: string;
  count: number;
  hourlyRate: number;
  annualCostPerResource: number;
  totalAnnualCost: number;
}

const TeamDetailModal: React.FC<TeamDetailModalProps> = ({ isOpen, onClose, team }) => {
  const [activeTab, setActiveTab] = useState<'roster' | 'cost'>('roster');
  const [assignments, setAssignments] = useState<ResourceTeamAssignment[]>([]);
  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && team) {
      loadTeamData();
    }
  }, [isOpen, team]);

  const loadTeamData = async () => {
    if (!team) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch active assignments for this team
      const assignmentsResponse = await resourceTeamAssignmentsApi.getAll({
        teamId: team.id,
        active: true,
      });

      if (assignmentsResponse.success) {
        setAssignments((assignmentsResponse.data as { assignments: ResourceTeamAssignment[] })?.assignments || []);
      }

      // Fetch current year rate cards
      const currentYear = new Date().getFullYear();
      const rateCardsResponse = await rateCardsApi.getByYear(currentYear);

      if (rateCardsResponse.success) {
        setRateCards((rateCardsResponse.data as { rateCards: RateCard[] })?.rateCards || []);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  if (!team) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Build roster with rate information
  const rosterData: RosterItem[] = assignments.map((assignment) => {
    const resource = assignment.resource;
    const rateCard = rateCards.find((rc) => rc.resourceLevelId === resource?.resourceLevelId);

    const hourlyRate = rateCard?.hourlyRate || null;
    const annualCost = hourlyRate ? hourlyRate * 40 * 52 : null;

    return {
      id: assignment.id,
      resourceName: resource ? `${resource.firstName} ${resource.lastName}` : 'Unknown',
      resourceId: resource?.resourceId || 'N/A',
      resourceType: resource?.resourceType || 'Unknown',
      resourceLevel: resource?.resourceLevel?.code || 'Unknown',
      startDate: assignment.startDate,
      hourlyRate,
      annualCost,
    };
  });

  // Calculate cost breakdown by resource level
  const costBreakdownMap = new Map<string, CostBreakdown>();

  rosterData.forEach((item) => {
    const { resourceLevel, hourlyRate, annualCost } = item;

    if (!hourlyRate || !annualCost) return;

    if (costBreakdownMap.has(resourceLevel)) {
      const breakdown = costBreakdownMap.get(resourceLevel)!;
      breakdown.count += 1;
      breakdown.totalAnnualCost += annualCost;
    } else {
      costBreakdownMap.set(resourceLevel, {
        level: resourceLevel,
        count: 1,
        hourlyRate,
        annualCostPerResource: annualCost,
        totalAnnualCost: annualCost,
      });
    }
  });

  const costBreakdown = Array.from(costBreakdownMap.values()).sort((a, b) =>
    a.level.localeCompare(b.level)
  );

  const totalTeamCost = costBreakdown.reduce((sum, item) => sum + item.totalAnnualCost, 0);

  // Roster table columns
  const rosterColumns: Column<RosterItem>[] = [
    {
      key: 'resourceName',
      header: 'Resource',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground text-sm">{value as string}</div>
          <div className="text-xs text-muted-foreground font-mono">{row.resourceId}</div>
        </div>
      ),
    },
    {
      key: 'resourceType',
      header: 'Type',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-foreground">
          {(value as string).replace('_', ' ')}
        </div>
      ),
    },
    {
      key: 'resourceLevel',
      header: 'Level',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-sm text-foreground">{value as string}</span>
      ),
    },
    {
      key: 'hourlyRate',
      header: 'Rate/hr',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-foreground">
          {value ? `$${value}` : 'N/A'}
        </div>
      ),
    },
    {
      key: 'annualCost',
      header: 'Annual Cost',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-foreground">
          {value ? formatCurrency(value as number) : 'N/A'}
        </div>
      ),
    },
    {
      key: 'startDate',
      header: 'Start Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1 text-sm text-foreground">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(value as string)}
        </div>
      ),
    },
  ];

  // Cost breakdown table columns
  const costColumns: Column<CostBreakdown>[] = [
    {
      key: 'level',
      header: 'Resource Level',
      sortable: true,
      render: (value) => <span className="font-mono font-medium">{value as string}</span>,
    },
    {
      key: 'count',
      header: 'Count',
      sortable: true,
      render: (value) => <div className="text-center">{value as number}</div>,
    },
    {
      key: 'hourlyRate',
      header: 'Rate/hr',
      sortable: true,
      render: (value) => <div>${value as number}/hr</div>,
    },
    {
      key: 'annualCostPerResource',
      header: 'Annual Cost (per resource)',
      sortable: true,
      render: (value) => formatCurrency(value as number),
    },
    {
      key: 'totalAnnualCost',
      header: 'Total Annual Cost',
      sortable: true,
      render: (value) => (
        <div className="font-semibold text-accent-blue">{formatCurrency(value as number)}</div>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Team: ${team.name}`} size="2xl">
      <div className="space-y-6">
        {/* Team Overview */}
        <GlassCard variant="subtle" padding="md" className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-1">Program</div>
            <div className="text-sm font-medium text-foreground">
              {team.program?.name || 'Unknown'}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-1">
              Team Members
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-foreground">
              <Users className="w-4 h-4" />
              {rosterData.length}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase mb-1">
              Actual Annual Cost
            </div>
            <div className="flex items-center gap-1 text-sm font-semibold text-accent-blue">
              <DollarSign className="w-4 h-4" />
              {formatCurrency(totalTeamCost)}
            </div>
          </div>
        </GlassCard>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('roster')}
              className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'roster'
                  ? 'border-accent-blue text-accent-blue bg-white/30 dark:bg-white/5 backdrop-blur-sm rounded-t-lg'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Team Roster
              </div>
            </button>
            <button
              onClick={() => setActiveTab('cost')}
              className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'cost'
                  ? 'border-accent-blue text-accent-blue bg-white/30 dark:bg-white/5 backdrop-blur-sm rounded-t-lg'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Cost Breakdown
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="bg-status-error/10 border border-status-error/30 text-status-error px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : (
          <>
            {/* Team Roster Tab */}
            {activeTab === 'roster' && (
              <div className="space-y-4">
                {rosterData.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-foreground">No team members assigned</p>
                  </div>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-[var(--border)] bg-white/40 dark:bg-white/5 backdrop-blur-sm shadow-sm">
                    <DataTable
                      data={rosterData}
                      columns={rosterColumns}
                      searchable
                      searchPlaceholder="Search team members..."
                      sortable
                      pagination
                      pageSize={10}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Cost Breakdown Tab */}
            {activeTab === 'cost' && (
              <div className="space-y-4">
                {costBreakdown.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-foreground">
                      No cost data available. Assign resources to view cost breakdown.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="rounded-xl overflow-hidden border border-[var(--border)] bg-white/40 dark:bg-white/5 backdrop-blur-sm shadow-sm">
                      <DataTable
                        data={costBreakdown}
                        columns={costColumns}
                        sortable
                        emptyMessage="No cost data available"
                      />
                    </div>

                    {/* Total Summary */}
                    <GlassCard variant="default" padding="md" className="flex items-center justify-between">
                      <div className="font-semibold text-foreground">
                        Total Actual Annual Cost
                      </div>
                      <div className="text-2xl font-bold text-accent-blue">
                        {formatCurrency(totalTeamCost)}
                      </div>
                    </GlassCard>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TeamDetailModal;
